import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipForward, SkipBack, Music2, Volume2, VolumeX } from "lucide-react";
import { getMusic, API } from "@/lib/api";

const ORIGIN = API.replace(/\/api$/, "");

export const MusicPlayer = () => {
  const audioRef = useRef(null);
  const userPausedRef = useRef(false);
  const autoMutedRef = useRef(false);
  const [tracks, setTracks] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    getMusic().then(setTracks).catch(() => {});
  }, []);

  const current = tracks[idx];
  const src = current ? (current.url.startsWith("http") ? current.url : ORIGIN + current.url) : "";

  // Try to start as early as possible when a track is available.
  // If audible autoplay is blocked, start muted (allowed) so it begins immediately.
  useEffect(() => {
    if (!src || !audioRef.current) return;
    const a = audioRef.current;
    a.volume = 0.5;
    a.muted = muted;
    a.play()
      .then(() => {
        setPlaying(true);
        setBlocked(false);
      })
      .catch(() => {
        autoMutedRef.current = true;
        a.muted = true;
        a.play()
          .then(() => {
            setPlaying(true);
            setBlocked(true); // playing but silent until first gesture
          })
          .catch(() => {
            setBlocked(true);
            setPlaying(false);
          });
      });
  }, [src]); // eslint-disable-line

  // On the first user gesture: unmute (if we auto-muted) and ensure playback
  useEffect(() => {
    const handler = () => {
      const a = audioRef.current;
      if (!a || userPausedRef.current) return;
      if (autoMutedRef.current && !muted) {
        a.muted = false;
        autoMutedRef.current = false;
        setBlocked(false);
      }
      if (a.paused) a.play().catch(() => {});
    };
    window.addEventListener("pointerdown", handler);
    window.addEventListener("keydown", handler);
    window.addEventListener("scroll", handler, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("keydown", handler);
      window.removeEventListener("scroll", handler);
    };
  }, [muted]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      userPausedRef.current = true;
      a.pause();
      setPlaying(false);
    } else {
      userPausedRef.current = false;
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const next = () => setIdx((i) => (i + 1) % Math.max(tracks.length, 1));
  const prev = () => setIdx((i) => (i - 1 + tracks.length) % Math.max(tracks.length, 1));

  const toggleMute = () => {
    const a = audioRef.current;
    setMuted((m) => {
      const nm = !m;
      if (a) a.muted = nm;
      if (!nm) autoMutedRef.current = false;
      return nm;
    });
  };

  if (!current) return null;

  return (
    <div data-testid="music-player" className="fixed bottom-4 left-4 z-[60]">
      <audio
        ref={audioRef}
        src={src}
        autoPlay
        onEnded={next}
        loop={tracks.length <= 1}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        preload="auto"
      />

      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="music-open-btn"
          aria-label="Müzik çaları aç"
          className={`relative flex items-center gap-3 rounded-full border border-accent/60 bg-[#1F1510]/90 backdrop-blur-md pl-2 pr-4 py-2 text-[#EADFCB] shadow-lg ${
            blocked ? "animate-pulse" : ""
          }`}
        >
          <span
            className={`flex items-center justify-center w-9 h-9 rounded-full bg-accent/20 text-accent ${
              playing ? "animate-spin" : ""
            }`}
            style={{ animationDuration: "6s" }}
          >
            <Music2 className="w-4 h-4" />
          </span>
          <span className="hidden sm:block text-left leading-tight">
            <span className="block text-[0.65rem] uppercase tracking-widest text-accent">
              {playing && blocked ? "Dokununca sesli" : playing ? "Çalıyor" : blocked ? "Müziği başlat" : "Duraklatıldı"}
            </span>
            <span className="block text-sm font-[Cinzel]">{current.title}</span>
          </span>
        </button>
      )}

      {open && (
        <div className="w-[min(90vw,300px)] rounded-xl border border-accent/50 bg-[#1F1510]/95 backdrop-blur-md p-4 text-[#EADFCB] shadow-2xl">
          <div className="flex items-center justify-between">
            <span className="text-[0.65rem] uppercase tracking-[0.2em] text-accent flex items-center gap-2">
              <Music2 className="w-3.5 h-3.5" /> Türkü Meclisi
            </span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Kapat"
              className="text-[#9B8A72] hover:text-[#EADFCB] text-lg leading-none"
            >
              ×
            </button>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <span
              className={`flex items-center justify-center w-12 h-12 rounded-full bg-accent/15 text-accent shrink-0 ${
                playing ? "animate-spin" : ""
              }`}
              style={{ animationDuration: "6s" }}
            >
              <Music2 className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <p className="font-[Cinzel] text-base truncate">{current.title}</p>
              <p className="text-xs text-[#C9B99E] truncate">{current.subtitle}</p>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-center gap-5">
            <button onClick={prev} data-testid="music-prev-btn" aria-label="Önceki" className="text-[#C9B99E] hover:text-accent transition-colors">
              <SkipBack className="w-5 h-5" />
            </button>
            <button
              onClick={toggle}
              data-testid="music-toggle-btn"
              aria-label={playing ? "Duraklat" : "Çal"}
              className="flex items-center justify-center w-12 h-12 rounded-full bg-accent text-[#1F1510] hover:bg-accent/90 transition-colors"
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <button onClick={next} data-testid="music-next-btn" aria-label="Sonraki" className="text-[#C9B99E] hover:text-accent transition-colors">
              <SkipForward className="w-5 h-5" />
            </button>
            <button
              onClick={toggleMute}
              data-testid="music-mute-btn"
              aria-label="Sesi aç/kapat"
              className="text-[#C9B99E] hover:text-accent transition-colors ml-1"
            >
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>

          <div className="mt-4 space-y-1">
            {tracks.map((t, i) => (
              <button
                key={t.id}
                onClick={() => {
                  userPausedRef.current = false;
                  setIdx(i);
                }}
                data-testid={`music-track-${t.id}`}
                className={`w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors ${
                  i === idx ? "bg-accent/20 text-accent" : "text-[#C9B99E] hover:bg-white/5"
                }`}
              >
                {i + 1}. {t.title}
              </button>
            ))}
          </div>

          {blocked && (
            <p className="mt-3 text-center text-[0.7rem] text-accent">
              Sesi açmak için ekrana bir kez dokunun.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
